/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { ChunkedAzureData, pullChunkedAzureData } from 'src/core/services/azure/azureService';
import { AzureContainerSwagger, AzureStorageAccountSwagger } from 'src/core/services/azure/azureSwaggers';
import { AzureSubscriptionStore } from 'src/core/stores/AzureSubscriptionStore';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { getStorageUrl } from 'src/core/utils/api/storageUtils';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService, ServiceContext } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export type AzureResponse<T> = { value: T };
export type AzureContainerService = ReturnType<typeof getAzureContainerService>;
export type AzureServiceParams = Pick<AzureSubscriptionStore, 'id'> & {
    token: string;
    principalId: string;
    storageAccountName: string;
    resourceGroupName: string;
};

const ContainerHeaderName = 'x-ms-version';

export const parseContainerXmlResponse = (response: string): AzureContainerSwagger[] => {
    const parser = new DOMParser();
    const blobContainerList: AzureContainerSwagger[] = [];
    const xmlDoc = parser.parseFromString(response, 'text/xml');

    for (const element of xmlDoc.getElementsByTagName('Name')) {
        blobContainerList.push({ name: element.childNodes[0].nodeValue, id: '', properties: null });
    }

    return blobContainerList;
};

/**
 * Represents a service for fetching Azure
 * storage token , Azure storage accounts and
 * Azure blob containers .
 */
export const getAzureContainerService = createService(({ getEnvironment }: ServiceContext<object>) => {
    const { azureBaseUrl } = getEnvironment();

    const buildBlobServiceUrlOptions = (token: string) => {
        const options = luisRequestOptionsBuilder()
            .useContentType('xml')
            .useArmToken(token)
            .skipThrottling()
            .build();

        options.headers[ContainerHeaderName] = '2017-11-09';

        return options;
    };

    /**
     * Creates a container for the given tenant, subscription id, resource group name and storage account.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storageservices/create-container
     *
     * @param param0.token The token required to create storage account for.
     * @param param0.subscriptionId The subscription id required to create storage account for.
     * @param param0.resourceGroupName The resource group name required to create storage account for.
     * @param param0.storageAccountName The storage account name to create.
     * @returns A promise with the list of Azure storage accounts.
     */
    const createAzureContainer = async ({
        token,
        containerName,
        subscriptionId,
        resourceGroupName,
        storageAccountName
    }: {
        token: string;
        containerName: string;
        subscriptionId: string;
        resourceGroupName: string;
        storageAccountName: string;
    }): Promise<AzureContainerSwagger> => {
        const urlOptions = luisRequestOptionsBuilder()
            .useContentType('json')
            .useArmToken(token)
            .skipThrottling()
            .build();
        const url = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', subscriptionId)
            .resource('resourceGroups', resourceGroupName)
            .resource('providers', 'Microsoft.Storage')
            .resource('storageAccounts', storageAccountName)
            .resource('blobServices', 'default')
            .resource('containers', containerName)
            .queryParam('api-version', '2019-06-01')
            .build();

        const { data: account } = await httpService.put<AzureContainerSwagger>(url, { properties: { publicAccess: 'None' } }, urlOptions);

        return account;
    };

    /**
     * Gets Azure containers.
     * @docLink https://docs.microsoft.com/en-us/rest/api/storagerp/blob-containers/list
     *
     * @param param0.name The Azure storage account name
     * @param param0.token The Azure storage token to use to get the blob containers.
     * @returns A promise with the blob containers.
     */
    const getAzureContainersByStorageAccountName = async ({
        name,
        token,
        subscriptionId,
        resourceGroupName
    }: Pick<AzureServiceParams, 'token'> &
        Pick<AzureStorageAccountSwagger, 'name'> & { subscriptionId: string; resourceGroupName: string }): Promise<ChunkedAzureData<
        AzureContainerSwagger
    >> => {
        const urlOptions = buildBlobServiceUrlOptions(token);

        const getUrl = urlBuilder()
            .baseUrl(azureBaseUrl)
            .resource('subscriptions', subscriptionId)
            .resource('resourceGroups', resourceGroupName)
            .resource('providers', 'Microsoft.Storage')
            .resource('storageAccounts', name)
            .resource('blobServices', 'default')
            .resource('containers')
            .queryParam('api-version', '2021-04-01')
            .build();

        const chunkedBlobContainers = await pullChunkedAzureData<AzureContainerSwagger>(getUrl, urlOptions);

        return { ...chunkedBlobContainers, data: chunkedBlobContainers.data };
    };

    /**
     * Sets CORS rules individually for the user Azure Storage service (Blob)
     * This request is served through luis-server, this is to avoid the browser
     * raises the red flag and blocks the API request with the CORS policy error
     * since the call originates from the frontend domain which does not match the
     * value return from the call to azure to enable cors for the blob service.
     *
     * @param param0.name The Azure storage account name.
     * @param param0.token The Azure storage token to use to get the blob containers.
     * @returns A promise with the blob containers.
     */
    const setAzureBlobStorageCORSProperties = async ({
        name,
        token
    }: Pick<AzureServiceParams, 'token'> & Pick<AzureStorageAccountSwagger, 'name'>): Promise<void> => {
        const urlOptions = luisRequestOptionsBuilder()
            .useContentType('json')
            .build();

        urlOptions.headers[ContainerHeaderName] = '2017-11-09';

        const setBlobStorageCorsConfigUrl = urlBuilder()
            .resource('api')
            .resource('blobStorageConfig')
            .resource('enableCors')
            .resource('account', name)
            .build();

        await httpService.post<string>(setBlobStorageCorsConfigUrl, { accessToken: token }, urlOptions);
    };

    /**
     * Enables the storage account CORS policies and returns once the access works in the data plane APIs.
     *
     * @param param0.storageToken The token used to access the storage account.
     * @param param0.storageAccountName The storage account name.
     */
    const setAndCheckAzureBlobStorageCORSPolicies = async ({
        storageToken,
        storageAccountName
    }: {
        storageToken: string;
        storageAccountName: string;
    }): Promise<void> => {
        await setAzureBlobStorageCORSProperties({ name: storageAccountName, token: storageToken });

        let isEnabled = false;
        let trialCount = 5;
        while (!isEnabled) {
            await new Promise(resolve => setTimeout(resolve, OperationDelayStrategies.exponentialDelayStrategy(null, trialCount++)));
            isEnabled = await checkStorageCORS({ storageAccountName, storageToken });
        }
    };

    /**
     * Checks if the storage account CORS settings is valid or not.
     *
     * @param param0.storageToken The token used to access the storage account.
     * @param param0.storageAccountName The storage account name.
     * @returns promise of boolean determines if the CORS is valid or not.
     */
    const checkStorageCORS = async ({
        storageToken,
        storageAccountName
    }: {
        storageToken: string;
        storageAccountName: string;
    }): Promise<boolean> => {
        const urlOptions = buildBlobServiceUrlOptions(storageToken);
        const blobUri = getStorageUrl({ storageAccountName, query: 'restype=service&comp=properties' });

        try {
            await httpService.get<string>(blobUri, urlOptions);

            return true;
        } catch (e) {
            // The CORS cause network failure error with a name Unknown because
            // the browser doesn't disclose any information about these kind of errors for security reasons,
            // This condition makes sure that the error caused by CORS issue.
            if ((<Error>e).name === LuisConstants.HttpResponseCodes.ClientSideFailure) {
                return false;
            }

            return true;
        }
    };

    return {
        checkStorageCORS,
        createAzureContainer,
        setAzureBlobStorageCORSProperties,
        getAzureContainersByStorageAccountName,
        setAndCheckAzureBlobStorageCORSPolicies
    };
});
